import { US } from "constants/countryCodes";
import { ONBOARDING_STEP, StepId } from "v2/components/onboarding/types";
import { OnboardingStepKey, toStepKey } from "v2/constants/onboardingStepKey";
import { EntityReviewItem } from "v2/store/onboardingEntityApi";
import { isEntityReviewComplete } from "./isEntityReviewComplete";

export type SignupAccountState = {
  hasAccount: boolean;
  hasUserDetails: boolean;
  isMobileVerified: boolean;
  isGroupCreated: boolean;
  hasEntityType: boolean;
  completedSteps: OnboardingStepKey[];
  reviewEntities?: EntityReviewItem[];
};

export type SignupProgress = {
  step: StepId;
  doneSteps: StepId[];
  isSignupComplete: boolean;
};

const STEP_ORDER: StepId[] = [
  ONBOARDING_STEP.ENTITY_TYPE,
  ONBOARDING_STEP.PERSONAL_DETAILS,
  ONBOARDING_STEP.PHONE,
  ONBOARDING_STEP.CONFIRM_ENTITY,
  ONBOARDING_STEP.REVIEW_ENTITY,
];

export const deriveSignupStep = ({
  hasAccount,
  hasUserDetails,
  isMobileVerified,
  isGroupCreated,
  hasEntityType,
  completedSteps,
  reviewEntities,
}: SignupAccountState): SignupProgress => {
  const usEntities = reviewEntities?.filter(
    (entity) => entity.countryCode === US
  );
  const primaryEntity = usEntities?.[0];

  const isRecorded = (id: StepId) => completedSteps.includes(toStepKey(id));

  const isVerificationInFlight = Boolean(
    usEntities?.some((entity) => entity.verificationPending)
  );

  const isReviewSubmitted = Boolean(
    isGroupCreated &&
      (isRecorded(ONBOARDING_STEP.REVIEW_ENTITY) || isVerificationInFlight)
  );

  const isReviewDataComplete = Boolean(
    primaryEntity && isEntityReviewComplete(primaryEntity)
  );

  const isStepDone: Record<StepId, boolean> = {
    [ONBOARDING_STEP.ENTITY_TYPE]:
      hasEntityType || isGroupCreated || hasUserDetails,
    [ONBOARDING_STEP.PERSONAL_DETAILS]: hasAccount,
    [ONBOARDING_STEP.PHONE]: hasAccount && isMobileVerified,
    [ONBOARDING_STEP.CONFIRM_ENTITY]:
      isGroupCreated &&
      (isRecorded(ONBOARDING_STEP.CONFIRM_ENTITY) ||
        isVerificationInFlight ||
        isReviewDataComplete),
    [ONBOARDING_STEP.REVIEW_ENTITY]: isReviewSubmitted,
  };

  const doneSteps = STEP_ORDER.filter((step) => isStepDone[step]);
  const pendingStep = STEP_ORDER.find((step) => !isStepDone[step]);

  return {
    step: pendingStep ?? ONBOARDING_STEP.REVIEW_ENTITY,
    doneSteps,
    isSignupComplete: isReviewSubmitted,
  };
};
